import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  useSpeechRecording,
  type SpeechRecordingErrorReason,
} from '../../../hooks/useSpeechRecording';
import type { LiksChatComposerSpeechInput } from './LiksChatComposer.types';

export function useLiksChatComposerSpeech(
  speechInput: LiksChatComposerSpeechInput | undefined,
  setMessage: Dispatch<SetStateAction<string>>,
) {
  const speechInputRef = useRef(speechInput);

  useEffect(() => {
    speechInputRef.current = speechInput;
  }, [speechInput]);

  const applyTranscription = useCallback(
    (text: string) => {
      const transcribed = text;
      const replaceDraftText =
        speechInputRef.current?.replaceDraftText === true;

      setMessage((current) => {
        if (replaceDraftText) {
          return transcribed;
        }
        if (current.length === 0) {
          return transcribed;
        }
        if (transcribed.length === 0) {
          return current;
        }
        if (current.endsWith(' ') || transcribed.startsWith(' ')) {
          return `${current}${transcribed}`;
        }
        return `${current} ${transcribed}`;
      });
    },
    [setMessage],
  );

  const handleSpeechRecordingReady = useCallback(
    async (file: File) => {
      const input = speechInputRef.current;
      if (input == null) {
        return;
      }
      const transcribed = await input.onTranscribe(file);
      applyTranscription(transcribed);
    },
    [applyTranscription],
  );

  const handleSpeechRecordingError = useCallback(
    (reason: SpeechRecordingErrorReason) => {
      const input = speechInputRef.current;
      if (input == null) {
        return;
      }
      if (reason === 'permission-denied') {
        input.onPermissionDenied();
        return;
      }
      if (reason === 'no-speech') {
        input.onNoSpeechDetected();
        return;
      }
      input.onMicrophoneUnavailable();
    },
    [],
  );

  const {
    status: speechStatus,
    elapsedMs: speechElapsedMs,
    isRecording: isSpeechRecording,
    toggleRecording: toggleSpeechRecording,
  } = useSpeechRecording({
    onRecordingReady: handleSpeechRecordingReady,
    onError: handleSpeechRecordingError,
  });

  const isSpeechRequesting = speechStatus === 'requesting';
  const isSpeechTranscribing = speechStatus === 'finalizing';
  const isSpeechSessionActive = isSpeechRecording || isSpeechRequesting;

  return {
    speechElapsedMs,
    isSpeechRecording,
    isSpeechRequesting,
    isSpeechTranscribing,
    isSpeechSessionActive,
    toggleSpeechRecording,
  };
}
